import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAgents, getSessions, getTasks, getTools, getWorkflows } from '../../../app/services/apiEndpoints';
import { Artifact } from '../utils';
import { IAgentConfig, ISessionConfig, ITaskConfig, IToolsConfig, IWorkflowConfig } from '../types';

interface IFetchArtifactPayload {
  group: string
}

export const fetchAgents = createAsyncThunk(
  'artifacts/agents',
  async (payload: IFetchArtifactPayload, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams({
        group: payload.group,
      });
      const response = await getAgents(queryParams);
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const fetchTasks = createAsyncThunk(
  'artifacts/tasks',
  async (payload: IFetchArtifactPayload, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams({
        group: payload.group,
      });
      const response = await getTasks(queryParams);
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const fetchWorkflows = createAsyncThunk(
  'artifacts/workflows',
  async (payload: IFetchArtifactPayload, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams({
        group: payload.group,
      });
      const response = await getWorkflows(queryParams);
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const fetchTools = createAsyncThunk(
  'artifacts/tools',
  async (_, thunkAPI) => {
    try {
      const response = await getTools();
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const fetchSessions = createAsyncThunk(
  'artifacts/sessions',
  async (payload: IFetchArtifactPayload, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams({
        group: payload.group,
      });
      const response = await getSessions(queryParams);
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

interface IArtifactsState {
  agents: IAgentConfig[] | null
  tasks: ITaskConfig[] | null
  workflows: IWorkflowConfig[] | null
  tools: IToolsConfig[] | null
  sessions: ISessionConfig[] | null
  selectedAgent: IAgentConfig | null
  selectedTask: ITaskConfig | null
  selectedWorkflow: IWorkflowConfig | null
  selectedSession: ISessionConfig | null
  currentArtifactFocus: string
  artifactsLoading: boolean
}

const initialState: IArtifactsState = {
  agents: [],
  tasks: [],
  workflows: [],
  tools: [],
  sessions: [],
  selectedAgent: null,
  selectedTask: null,
  selectedWorkflow: null,
  selectedSession: null,
  currentArtifactFocus: Artifact.Workflow,
  artifactsLoading: false
};

const artifactsSlice = createSlice({
  name: 'artifacts',
  initialState,
  reducers: {
    setAgents(state, action) {
      state.agents = action.payload;
    },
    insertAgent(state, action) {
      state.agents?.push(action.payload);
    },
    upsertAgent(state, action) {
      let index = state.agents?.findIndex(item => item.sk === action.payload.sk);
      if (index !== -1 && index !== undefined) {
        state.agents![index] = {
          ...state.agents![index],
          ...action.payload
        };
      } else {
        state.agents?.push(action.payload);
      }
    },
    setTasks(state, action) {
      state.tasks = action.payload;
    },
    insertTask(state, action) {
      state.tasks?.push(action.payload);
    },
    upsertTask(state, action) {
      let index = state.tasks?.findIndex(item => item.sk === action.payload.sk);
      if (index !== -1 && index !== undefined) {
        state.tasks![index] = {
          ...state.tasks![index],
          ...action.payload
        };
      } else {
        state.tasks?.push(action.payload);
      }
    },
    setWorkflows(state, action) {
      state.workflows = action.payload;
    },
    insertWorkflow(state, action) {
      state.workflows?.push(action.payload);
    },
    upsertWorkflow(state, action) {
      let index = state.workflows?.findIndex(item => item.sk === action.payload.sk);
      if (index !== -1 && index !== undefined) {
        state.workflows![index] = {
          ...state.workflows![index],
          ...action.payload
        };
      } else {
        state.workflows?.push(action.payload);
      }
    },
    setTools(state, action) {
      state.tools = action.payload;
    },
    setSessions(state, action) {
      state.sessions = action.payload;
    },
    insertSession(state, action) {
      state.sessions = [...(Array.isArray(state.sessions) ? state.sessions : []), action.payload]
    },
    setSelectedAgent(state, action) {
      state.selectedAgent = action.payload;
    },
    setSelectedTask(state, action) {
      state.selectedTask = action.payload;
    },
    setSelectedWorkflow(state, action) {
      state.selectedWorkflow = action.payload;
    },
    setSelectedSession(state, action) {
      state.selectedSession = action.payload;
    },
    setCurrentArtifactFocus(state, action) {
      state.currentArtifactFocus = action.payload;
    },
    setArtifactsLoading(state, action) {
      state.artifactsLoading = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgents.pending, (state, action) => {
        state.artifactsLoading = true
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.agents = action.payload;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        console.log("Error occurred:", action.payload)
      })
      .addCase(fetchTasks.pending, (state, action) => {
        state.artifactsLoading = true
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.tasks = action.payload;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        console.log("Error occurred:", action.payload)
      })
      .addCase(fetchWorkflows.pending, (state, action) => {
        state.artifactsLoading = true
      })
      .addCase(fetchWorkflows.fulfilled, (state, action) => {
        state.workflows = action.payload;
      })
      .addCase(fetchWorkflows.rejected, (state, action) => {
        console.log("Error occurred:", action.payload)
      })
      .addCase(fetchTools.pending, (state, action) => {
        state.artifactsLoading = true
      })
      .addCase(fetchTools.fulfilled, (state, action) => {
        state.tools = action.payload;
      })
      .addCase(fetchTools.rejected, (state, action) => {
        console.log("Error occurred:", action.payload)
      })
      .addCase(fetchSessions.pending, (state, action) => {
        state.artifactsLoading = true
      })
      .addCase(fetchSessions.fulfilled, (state, action) => {
        state.sessions = action.payload;
      })
      .addCase(fetchSessions.rejected, (state, action) => {
        console.log("Error occurred:", action.payload)
      })
  },
});

export const {
  setAgents,
  insertAgent,
  upsertAgent,
  setTasks,
  insertTask,
  upsertTask,
  setWorkflows,
  insertWorkflow,
  upsertWorkflow,
  setTools,
  setSessions,
  insertSession,
  setSelectedAgent,
  setSelectedTask,
  setSelectedWorkflow,
  setSelectedSession,
  setCurrentArtifactFocus,
  setArtifactsLoading } = artifactsSlice.actions;
export default artifactsSlice.reducer;